import React, { ChangeEvent } from 'react';
import { Form, Input, Select, FormInstance, InputRef } from 'antd';
import { AsYouType, CountryCode } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';

import { onCountryChange, phoneValidator } from 'utils/misc';
import flagImages from 'utils/flagImages';
import { fieldDefaultProps } from 'utils/fieldConfig';

interface ICountry {
  id: number;
  isoAlpha2: string;
  phoneCountryCode: string;
  name: string;
}

interface ContactNumberInputProps {
  form: FormInstance;
  countries: ICountry[];
  name: string;
  label?: string;
  required?: boolean;
  requiredMessage?: string;
  placeholder?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  dataTestId?: string;
  readOnly?: boolean;
  selectedCountry: { code: CountryCode; iso: string | undefined };
  setSelectedCountry: React.Dispatch<React.SetStateAction<{ code: CountryCode; iso: string | undefined }>>;
}

const ContactInput: React.FC<ContactNumberInputProps> = ({
  form,
  countries,
  name,
  label,
  required = true,
  requiredMessage,
  placeholder,
  inputProps,
  dataTestId,
  readOnly = false,
  selectedCountry,
  setSelectedCountry,
}) => {
  const { t } = useTranslation();
  const inputRef = React.useRef<InputRef>(null);

  const handleCountryChange = (value: string) => {
    onCountryChange({
      value,
      data: { data: countries },
      form,
      setSelectedCountry,
    });
    form.setFieldsValue({
      [`${name}CountryCode`]: selectedCountry.code,
    });
    form.setFieldsValue({
      [`${name}CountryIso`]: selectedCountry.iso,
    });
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawPhoneNumber = input.value;
    const cursorPosition = input.selectionStart || 0;

    // Get the current formatted value
    const formatter = new AsYouType(selectedCountry.iso as CountryCode);
    const formattedPhone = formatter.input(rawPhoneNumber);

    // Calculate the new cursor position
    let newCursorPosition = cursorPosition;

    // If the formatted length is different from the raw length, adjust cursor position
    if (formattedPhone.length !== rawPhoneNumber.length) {
      // Count digits before the cursor in the original input
      const digitsBeforeCursor = rawPhoneNumber.slice(0, cursorPosition).replace(/\D/g, '').length;

      // Find the position in the formatted string that corresponds to the same number of digits
      let digitCount = 0;
      for (let i = 0; i < formattedPhone.length; i++) {
        if (/\d/.test(formattedPhone[i])) {
          digitCount++;
          if (digitCount > digitsBeforeCursor) {
            newCursorPosition = i;
            break;
          }
        }
        if (digitCount === digitsBeforeCursor) {
          newCursorPosition = i + 1;
        }
      }

      // Ensure cursor position doesn't exceed the formatted string length
      newCursorPosition = Math.min(newCursorPosition, formattedPhone.length);
    }

    form.setFieldsValue({
      [name]: formattedPhone,
    });

    // Restore cursor position after the value update
    setTimeout(() => {
      if (inputRef.current?.input) {
        inputRef.current.input.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  const validatePhoneNumber = (_: unknown, value: string) => {
    return phoneValidator(value, selectedCountry.iso as CountryCode, t);
  };

  return (
    <>
      <Form.Item name={`${name}CountryCode`} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={`${name}CountryIso`} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={`${name}`} hidden>
        <Input />
      </Form.Item>
      <Form.Item
        label={label ?? t('auth.signup.contact_number')}
        name={name}
        rules={[
          { required, message: requiredMessage ?? t('auth.signup.rules.phone_required') },
          { validator: validatePhoneNumber },
        ]}
        validateTrigger="onBlur"
      >
        <Input
          {...inputProps}
          {...fieldDefaultProps}
          ref={inputRef}
          onChange={handlePhoneChange}
          data-testid={dataTestId}
          placeholder={placeholder ?? t('auth.signup.contact_number_placeholder')}
          disabled={readOnly}
          addonBefore={
            <Select
              showSearch
              className="w-28"
              value={selectedCountry.code + ' ' + selectedCountry.iso}
              onChange={handleCountryChange}
              optionFilterProp="search-value"
              data-testid="Country-Code"
              disabled={readOnly || inputProps?.readOnly}
            >
              {countries.map((country) => (
                <Select.Option
                  data-testid="Country-Code"
                  key={country.id}
                  value={country.phoneCountryCode + ' ' + country.isoAlpha2}
                  {...fieldDefaultProps}
                  search-value={`${country.name} ${country.phoneCountryCode}`}
                >
                  <div className="flex items-center gap-2">
                    <img src={flagImages[country.isoAlpha2]} alt={country.name} className="w-4 h-3" />
                    <span dir="ltr">{country.phoneCountryCode}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          }
        />
      </Form.Item>
    </>
  );
};

export default ContactInput;
